// Button — the single source of truth for the orbital-lint button look.
//
// Before this primitive the same Tailwind string (`bg-accent text-accent-fg
// hover:opacity-90 …`) was copy-pasted across 17 files, with the secondary
// ("Cancel"-style) variant drifting independently. The variants below encode
// exactly those two shapes plus `danger` and `ghost`.
//
// `buttonClasses()` is exported separately so non-<button> elements that must
// look like buttons (notably react-router `<Link>`) can reuse the styling
// without wrapping a button in an anchor.
import { ButtonHTMLAttributes, forwardRef } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from './cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

const base =
  'inline-flex items-center justify-center gap-2 rounded-md font-semibold ' +
  'uppercase tracking-[0.08em] transition ' +
  'disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none';

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-[11px]',
  md: 'px-4 py-2 text-xs',
};

const variantClasses: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-accent-fg border border-accent/40 hover:opacity-90',
  secondary: 'text-fg-muted hover:text-fg hover:bg-bg-hover border border-border',
  ghost: 'text-fg-muted hover:text-fg hover:bg-bg-hover',
  danger: 'bg-danger text-white border border-danger/40 hover:opacity-90',
};

export function buttonClasses(opts?: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
}): string {
  const { variant = 'primary', size = 'md', className } = opts ?? {};
  return cn(base, sizeClasses[size], variantClasses[variant], className);
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  /** Shows a spinner and disables the button while truthy. */
  readonly loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading = false, disabled, className, children, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      // Default to type="button": an un-typed button inside a <form> submits it,
      // which is almost never what a toolbar/action button wants.
      type={type ?? 'button'}
      disabled={disabled || loading}
      className={buttonClasses({ variant, size, className })}
      {...rest}
    >
      {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : null}
      {children}
    </button>
  );
});
